/**
 * ShorelineField — the terrain-aware bed-depth cache for a finite water body
 * (the Crest-style "sea floor depth" data): a one-time bake of
 *
 *   depth(x, z) = waterLevel − bedHeight(x, z)     (clamped ≥ 0)
 *
 * over the water footprint, held as a CPU grid for exact height-query
 * attenuation AND uploaded as a single-channel DataTexture the material
 * samples in plane UV (same grid/orientation as FoamAccumulator).
 *
 * It drives three effects:
 *  - wave SHOALING: displacement fades as depth → 0 so swell dies on the
 *    beach instead of clipping through it (applied in the shader AND in
 *    WaterSystem.getWaterHeightAt — physics matches visuals);
 *  - shallow tinting: the water column pulls toward a sand tint where the
 *    bed nears the surface, independent of the view-depth buffer;
 *  - beach foam: a surf band where bed depth < foamDepth, visible from any
 *    viewpoint (the depth-buffer shore foam needs the floor on screen).
 */

import * as THREE from 'three/webgpu';

export interface ShorelineFieldConfig {
  /** Water footprint in world units. */
  sizeX: number;
  sizeZ: number;
  /** Calm water surface height (world Y). */
  waterLevel: number;
  /** Terrain height at world (x, z) — e.g. TerrainSystem.sampleHeightAt. */
  bedHeight: (x: number, z: number) => number;
  /** Grid resolution per side. Default 128 (clamped 16..512). */
  resolution?: number;
  /** Depth below which waves attenuate (shoaling). Default 6 m. */
  shoalDepth?: number;
  /** Bed depth covered by the surf/foam band. Default 1.2 m. */
  foamDepth?: number;
  /**
   * World offset added to the local sample positions when calling
   * `bedHeight` — lets a FOLLOWING water body (recentered on the player)
   * bake the footprint around any world position. See rebake().
   */
  offsetX?: number;
  offsetZ?: number;
}

export class ShorelineField {
  /** R = bedDepth/encodeMax (Uint8), plane-UV addressed like the foam texture. */
  readonly texture: THREE.DataTexture;
  /** Metres encoded at R = 1 (depths clamp here; beyond it nothing changes). */
  readonly encodeMax: number;
  readonly shoalDepth: number;
  readonly foamDepth: number;

  private readonly depthGrid: Float32Array;
  private readonly bytes: Uint8Array;
  private readonly res: number;
  private readonly sizeX: number;
  private readonly sizeZ: number;
  private readonly waterLevel: number;
  private readonly bedHeight: (x: number, z: number) => number;
  /** Depth of the deepest baked texel (m) — 0 means the footprint is dry. */
  maxDepth = 0;

  constructor(config: ShorelineFieldConfig) {
    const n = Math.min(512, Math.max(16, Math.floor(config.resolution ?? 128)));
    this.res = n;
    this.sizeX = config.sizeX;
    this.sizeZ = config.sizeZ;
    this.waterLevel = config.waterLevel;
    this.bedHeight = config.bedHeight;
    this.shoalDepth = Math.max(0.01, config.shoalDepth ?? 6);
    this.foamDepth = Math.max(0.01, config.foamDepth ?? 1.2);
    // Headroom above the shoal band so the shallow tint still grades in
    // deeper water; Uint8 gives ~0.1 m steps at the default 6 m shoal.
    this.encodeMax = this.shoalDepth * 4;

    this.depthGrid = new Float32Array(n * n);
    this.bytes = new Uint8Array(n * n);

    this.texture = new THREE.DataTexture(this.bytes, n, n, THREE.RedFormat);
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.colorSpace = THREE.NoColorSpace;

    this.rebake(config.offsetX ?? 0, config.offsetZ ?? 0);
  }

  /**
   * Re-sample the bed around a new world offset into the SAME texture —
   * a following water body (recentered on the player) rebakes here instead
   * of rebuilding its material: the shader closure keeps this texture, only
   * its texels change. Also refreshes CPU depth queries and `maxDepth`.
   */
  rebake(offsetX = 0, offsetZ = 0): void {
    const n = this.res;
    let deepest = 0;
    for (let row = 0; row < n; row++) {
      const z = (row / (n - 1) - 0.5) * this.sizeZ + offsetZ;
      for (let col = 0; col < n; col++) {
        const x = (col / (n - 1) - 0.5) * this.sizeX + offsetX;
        const depth = Math.max(0, this.waterLevel - this.bedHeight(x, z));
        const i = row * n + col;
        this.depthGrid[i] = depth;
        this.bytes[i] = Math.round(Math.min(1, depth / this.encodeMax) * 255);
        if (depth > deepest) deepest = depth;
      }
    }
    this.maxDepth = deepest;
    this.texture.needsUpdate = true;
  }

  /** Bed depth (m, ≥ 0) at local plane coordinates — bilinear. */
  depthAt(localX: number, localZ: number): number {
    const n = this.res;
    const gx = Math.min(n - 1, Math.max(0, (localX / this.sizeX + 0.5) * (n - 1)));
    const gz = Math.min(n - 1, Math.max(0, (localZ / this.sizeZ + 0.5) * (n - 1)));
    const x0 = Math.floor(gx);
    const z0 = Math.floor(gz);
    const x1 = Math.min(x0 + 1, n - 1);
    const z1 = Math.min(z0 + 1, n - 1);
    const fx = gx - x0;
    const fz = gz - z0;
    const d00 = this.depthGrid[z0 * n + x0];
    const d10 = this.depthGrid[z0 * n + x1];
    const d01 = this.depthGrid[z1 * n + x0];
    const d11 = this.depthGrid[z1 * n + x1];
    const d0 = d00 + (d10 - d00) * fx;
    const d1 = d01 + (d11 - d01) * fx;
    return d0 + (d1 - d0) * fz;
  }

  /**
   * Wave shoaling factor 0..1 at local plane coordinates: 0 on dry land,
   * 1 at/beyond shoalDepth. The smoothstep MIRRORS the shader's — the CPU
   * height query and the displaced mesh must agree.
   */
  attenuationAt(localX: number, localZ: number): number {
    const t = Math.min(1, Math.max(0, this.depthAt(localX, localZ) / this.shoalDepth));
    return t * t * (3 - 2 * t);
  }

  dispose(): void {
    this.texture.dispose();
  }
}
